// =============================================================================
// APP — Root Application Component for Le Coup de Torchon
// =============================================================================
// Main application layout composing:
// SkipToContent → Navbar → Hero → Menu → Ambiance Showcase → About → Gallery → Contact & Map → Footer
// + CookieConsent & LegalModal (Privacy, Terms, Accessibility, Cookie Settings)
// Fully localized across 6 languages (FR, EN, ZH, DE, IT, PT).
// =============================================================================

import { useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';
import {
  SkipToContent,
  Navbar,
  Hero,
  MenuSection,
  Footer,
  CookieConsent,
  LegalModal,
  CTAButton,
  LanguageSwitcher,
  DeliveryButtons,
  AtmosphereBackground,
  generateRestaurantSchema,
  type NavLink,
  type Language,
  type LegalModalType,
  type GuideLinkItem,
  type ReservationLinkItem,
  type AtmosphereLayer,
} from '@webzite/core';
import { OpenStreetMapEmbed, GoogleMapsEmbed } from '@webzite/integrations';
import { validateRestaurant, validateMenu } from '@webzite/content-schema';

// Import content data
import restaurantJson from '../content/restaurant.json';
import menuJson from '../content/menu.json';
import siteConfig from '../site.config.js';

// Validate content at runtime
const restaurant = validateRestaurant(restaurantJson);
const menu = validateMenu(menuJson);

export function App() {
  const { t, i18n } = useTranslation();
  const [activeModal, setActiveModal] = useState<LegalModalType>(null);

  // --- Navigation links ---
  const navLinks: NavLink[] = [
    { id: 'menu', label: t('nav.menu'), href: '#menu' },
    { id: 'ambiance', label: t('nav.about'), href: '#about' },
  ];

  if (restaurant.features.gallery && restaurant.gallery.length > 0) {
    navLinks.push({ id: 'gallery', label: t('nav.gallery'), href: '#gallery' });
  }

  navLinks.push({ id: 'contact', label: t('nav.contact'), href: '#contact' });

  // --- Languages ---
  const languages: Language[] = siteConfig.locales.map((code) => ({
    code,
    label: code.toUpperCase(),
  }));

  const handleLanguageChange = useCallback(
    (locale: string) => {
      void i18n.changeLanguage(locale);
    },
    [i18n],
  );

  const reservationUrl = siteConfig.reservations.bookingUrl ?? `tel:${restaurant.phone.replace(/\s+/g, '')}`;
  const localizedTagline = t('hero.tagline', { defaultValue: restaurant.tagline });
  const localizedStory = restaurant.about
    ? t('about.story', { defaultValue: restaurant.about.story })
    : undefined;

  // Gastronomic guides & reviews references requested by client
  const guideLinks: GuideLinkItem[] = [
    {
      label: 'TripAdvisor (Avis & Notes)',
      url: 'https://www.tripadvisor.fr/Restaurant_Review-g187147-d2005462-Reviews-Le_Coup_de_Torchon-Paris_Ile_de_France.html',
    },
    {
      label: 'Guide Boullenger',
      url: 'https://www.guideboullenger.com/le-coup-de-torchon-f1399505.html',
    },
    {
      label: 'LesRestos.com (Critique Gastronomique)',
      url: 'https://www.lesrestos.com/critiques/tous-les-gouts/passionnement/article/au-coup-de-torchon',
    },
  ];

  const reservationLinks: ReservationLinkItem[] = [
    {
      label: `${restaurant.phone}`,
      url: `tel:${restaurant.phone.replace(/\s+/g, '')}`,
    },
  ];

  // Dynamic atmospheric background layers that crossfade as user scrolls
  const atmosphereLayers: AtmosphereLayer[] = [
    {
      sectionId: 'hero',
      src: '/images/interior-arched-room.png',
      alt: 'Salle voûtée et murs de velours rouge feutré',
    },
    {
      sectionId: 'menu',
      src: '/images/interior-cozy-lamp.png',
      alt: 'Ambiance feutrée avec lampe ambrée et livres anciens',
    },
    {
      sectionId: 'ambiance-showcase',
      src: '/images/interior-sunflower-corner.png',
      alt: 'Table intimiste sous tableau de tournesols',
    },
    {
      sectionId: 'about',
      src: '/images/interior-textured-wall.png',
      alt: 'Murs texturés et appliques en laiton doré',
    },
    {
      sectionId: 'gallery',
      src: '/images/interior-dining-tables.png',
      alt: 'Tables soignées nappées de bordeaux',
    },
    {
      sectionId: 'contact',
      src: '/images/interior-wine-lamp.png',
      alt: 'Lampe bouteille de grand cru et accueil chaleureux',
    },
  ];

  return (
    <>
      {/* Skip-to-content link for keyboard accessibility */}
      <SkipToContent />

      {/* Dynamic scrolling atmosphere background crossfading through authentic interiors */}
      <AtmosphereBackground layers={atmosphereLayers} />

      {/* Navigation bar (glassmorphic dark velvet styling, sticky on scroll) */}
      <Navbar
        siteName={restaurant.name}
        links={navLinks}
        ctaText={restaurant.features.reservations ? t('nav.reserve') : undefined}
        ctaHref={restaurant.features.reservations ? reservationUrl : undefined}
        languageSwitcher={
          <LanguageSwitcher
            currentLocale={i18n.language}
            languages={languages}
            onLanguageChange={handleLanguageChange}
          />
        }
      />

      {/* Main content */}
      <main id="main-content">
        {/* Hero section with high-resolution red velvet arched room interior */}
        <Hero
          headline={restaurant.name}
          tagline={localizedTagline}
          backgroundImage="/images/interior-arched-room.png"
          backgroundAlt="Intérieur chaleureux et murs de velours rouge du restaurant Le Coup de Torchon"
          transparentBackground={true}
          primaryCTA={
            restaurant.features.reservations ? (
              <CTAButton as="link" href={reservationUrl} variant="primary">
                {t('hero.cta.reserve')}
              </CTAButton>
            ) : undefined
          }
          secondaryCTA={
            <CTAButton as="link" href="#menu" variant="secondary">
              {t('hero.cta.menu')}
            </CTAButton>
          }
        />

        {/* Menu section (formulas, starters, mains, desserts, drinks with allergens) */}
        <MenuSection
          menu={menu}
          currency={siteConfig.currency}
          locale={i18n.language}
          heading={t('menu.heading')}
          allergenNote={t('menu.allergens.note')}
        />

        {/* Ambiance showcase strip — rolling interior view as user scrolls down */}
        <section className="section ambiance-section" id="ambiance-showcase" aria-label="Ambiance du restaurant">
          <div className="container">
            <h2 className="text-center">{t('about.heading')}</h2>
            <p className="text-center text-label" style={{ color: 'var(--color-accent)' }}>
              Paris 5e · Depuis 1988
            </p>

            <div className="ambiance-grid">
              <div className="ambiance-card">
                <img
                  src="/images/interior-arched-room.png"
                  alt="Salle voûtée et murs de velours rouge feutré"
                  className="ambiance-card-image"
                  loading="lazy"
                />
                <div className="ambiance-card-overlay">
                  <h3 className="ambiance-card-title">Atmosphère Feutrée</h3>
                  <p className="ambiance-card-text">Voûte en pierre de taille et tentures de velours bordeaux</p>
                </div>
              </div>

              <div className="ambiance-card">
                <img
                  src="/images/interior-cozy-lamp.png"
                  alt="Lampe chaleureuse ambrée, livres d'époque et tableaux"
                  className="ambiance-card-image"
                  loading="lazy"
                />
                <div className="ambiance-card-overlay">
                  <h3 className="ambiance-card-title">Éclairage Chaleureux</h3>
                  <p className="ambiance-card-text">Lumière ambrée, livres anciens et charme authentique</p>
                </div>
              </div>

              <div className="ambiance-card">
                <img
                  src="/images/interior-sunflower-corner.png"
                  alt="Table intimiste sous tableau de tournesols"
                  className="ambiance-card-image"
                  loading="lazy"
                />
                <div className="ambiance-card-overlay">
                  <h3 className="ambiance-card-title">Tradition Bistrot</h3>
                  <p className="ambiance-card-text">Tables soignées et accueil chaleureux au cœur du Quartier Latin</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About / Story section */}
        {restaurant.about && (
          <section className="section about-section" id="about" aria-labelledby="about-heading">
            <div className="container container-narrow text-center">
              <h2 id="about-heading">{t('about.heading')}</h2>
              <p className="about-story-text">{localizedStory}</p>
              {restaurant.about.chefName && (
                <p className="about-chef-signature">— {restaurant.about.chefName}</p>
              )}
            </div>
          </section>
        )}

        {/* Gallery Section */}
        {restaurant.features.gallery && restaurant.gallery.length > 0 && (
          <section className="section gallery-section" id="gallery" aria-labelledby="gallery-heading">
            <div className="container">
              <h2 id="gallery-heading" className="text-center">
                {t('gallery.heading')}
              </h2>
              <div className="gallery-grid-layout">
                {restaurant.gallery.map((img, idx) => (
                  <div key={idx} className="gallery-card-item">
                    <img
                      src={img.src}
                      alt={img.alt}
                      loading="lazy"
                      className="gallery-card-image"
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Contact, Reservations & Map section */}
        <section className="section contact-section" id="contact" aria-labelledby="contact-heading">
          <div className="container">
            <h2 id="contact-heading" className="text-center">
              {t('contact.heading')}
            </h2>

            <div className="contact-grid-layout">
              {/* Contact Information & Localized Hours */}
              <div>
                <h3>{t('contact.address')}</h3>
                <address className="contact-address-box">
                  <p className="contact-street">{restaurant.address.streetAddress}</p>
                  <p className="contact-locality">
                    {restaurant.address.postalCode} {restaurant.address.addressLocality}
                  </p>
                  <p className="contact-detail-row">
                    <strong>{t('contact.phone')}:</strong>{' '}
                    <a href={`tel:${restaurant.phone.replace(/\s+/g, '')}`}>{restaurant.phone}</a>
                  </p>
                  <p>
                    <strong>{t('contact.email')}:</strong>{' '}
                    <a href={`mailto:${restaurant.email}`}>{restaurant.email}</a>
                  </p>
                </address>

                {/* Online Reservations CTAs */}
                {restaurant.features.reservations && (
                  <div className="contact-reservations-wrapper">
                    <h4>{t('nav.reserve')}</h4>
                    <div className="contact-reservations-buttons">
                      <CTAButton as="link" href={reservationUrl} variant="primary">
                        📞 {restaurant.phone}
                      </CTAButton>
                    </div>
                  </div>
                )}

                <h3 style={{ marginBlockStart: 'var(--space-8)' }}>{t('contact.hours')}</h3>
                <ul className="contact-hours-list" role="list">
                  {restaurant.hours.map((spec, i) => {
                    const localizedDays = spec.dayOfWeek
                      .map((d) => t(`days.${d.toLowerCase()}`, { defaultValue: d }))
                      .join(', ');

                    return (
                      <li key={i} className="contact-hours-item">
                        <strong>{localizedDays}</strong>: {spec.opens} – {spec.closes}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Interactive Map Embed with Localized Labels */}
              <div>
                <h3>{t('contact.map', 'Plan')}</h3>
                <div className="contact-map-wrapper">
                  {siteConfig.maps.provider === 'google' ? (
                    <GoogleMapsEmbed
                      embedUrl={siteConfig.maps.embedUrl ?? ''}
                      googleMapsLink={siteConfig.maps.googleMapsLink}
                      title={restaurant.name}
                      loadButtonText={t('map.click')}
                      openGoogleMapsText={t('map.open.google')}
                    />
                  ) : (
                    <OpenStreetMapEmbed
                      latitude={restaurant.geo.latitude}
                      longitude={restaurant.geo.longitude}
                      address={`${restaurant.address.streetAddress}, ${restaurant.address.postalCode} ${restaurant.address.addressLocality}`}
                      embedUrl={siteConfig.maps.embedUrl}
                      googleMapsLink={siteConfig.maps.googleMapsLink}
                      appleMapsLink={siteConfig.maps.appleMapsLink}
                      title={restaurant.name}
                      loadButtonText={t('map.click')}
                      openGoogleMapsText={t('map.open.google')}
                      openAppleMapsText={t('map.open.apple')}
                      variant="white"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Delivery buttons (if enabled) */}
        {siteConfig.delivery.enabled && siteConfig.delivery.platforms.length > 0 && (
          <section className="section text-center">
            <div className="container">
              <DeliveryButtons
                platforms={siteConfig.delivery.platforms}
                heading={t('delivery.heading')}
              />
            </div>
          </section>
        )}
      </main>

      {/* Footer (Localized hours, tagline, reservation links, guide links, legal links, developer attribution) */}
      <Footer
        restaurant={restaurant}
        tagline={localizedTagline}
        developer={siteConfig.developer}
        reservationLinks={reservationLinks}
        guideLinks={guideLinks}
        secondaryLinks={[
          { label: t('nav.menu'), href: '#menu' },
          { label: t('nav.about'), href: '#about' },
          { label: t('nav.contact'), href: '#contact' },
        ]}
        onOpenLegal={(type) => setActiveModal(type)}
        onOpenCookieSettings={() => setActiveModal('cookies')}
        labels={{
          hours: t('footer.hours'),
          contact: t('footer.contact'),
          quickLinks: t('footer.quicklinks'),
          guides: t('footer.guides'),
          privacy: t('footer.privacy'),
          terms: t('footer.terms'),
          accessibility: t('footer.accessibility'),
          cookieSettings: t('footer.cookies'),
          allRightsReserved: t('footer.rights'),
          craftedBy: t('footer.craftedBy'),
        }}
        deliveryButtons={
          siteConfig.delivery.enabled ? (
            <DeliveryButtons platforms={siteConfig.delivery.platforms} />
          ) : undefined
        }
      />

      {/* Legal & Cookie Settings Modals */}
      <LegalModal
        type={activeModal}
        onClose={() => setActiveModal(null)}
        restaurant={restaurant}
        locale={i18n.language}
      />

      {/* Cookie consent banner (GDPR) */}
      {(siteConfig.region === 'eu' || siteConfig.region === 'uk') && (
        <CookieConsent
          heading={t('cookies.heading')}
          description={t('cookies.description')}
          acceptText={t('cookies.accept')}
          rejectText={t('cookies.reject')}
          onOpenSettings={() => setActiveModal('cookies')}
        />
      )}

      {/* JSON-LD structured data for SEO & AI with developer creator attribution */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            generateRestaurantSchema(
              restaurant,
              'https://lecoupdetorchon-paris.fr',
              siteConfig.developer,
            ),
          ),
        }}
      />
    </>
  );
}
